import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { IApiInvestigationReportStatus, IApiReportWithStatus } from 'src/app/shared/modules/graphql/types/types';
import { getPriorityIcon, getPriorityTooltip, getRiskTypeIcon } from 'src/app/shared/helpers/reactive-form.class';
import { InvestigationReportStatus } from 'src/app/shared/services/investigation/investigation-report/investigation-report.service';

export type InvestigationTableCommonColumnType = 'investigation' | 'details' | 'current' | 'priority';

@Component({
  selector: 'app-investigation-table-common-columns',
  templateUrl: './investigation-table-common-columns.component.html',
  styleUrls: ['./investigation-table-common-columns.component.scss']
})
export class InvestigationTableCommonColumnsComponent {
  @Input() dataItem: any;
  @Input() type: InvestigationTableCommonColumnType = 'investigation';
  @Input() investigationCommentsCreateEnabled = false;

  @Output() commentAdd = new EventEmitter<any>();
  @Output() documentsReportsOpen = new EventEmitter<{ event: MouseEvent; investigation: any }>();

  constructor(private router: Router) { }

  private reportIconByTitle: Record<string, string> = {
    'Case Status Update': 'report-csu',
    'Expense Report': 'report-exp',
    'Investigation Report': 'report-inv',
    'Post Inspection Communication': 'report-pic',
    'Scene Protocol': 'report-p',
    'Addendum': 'report-a',
    'HVAC Report': 'report-h',
    'Cause & Origin Report': 'report-co',
    'Photo Sheet': 'report-ps',
    'Electrical Analysis Report': 'report-ee',
    'PIC-Report to Follow': 'report-pic-r',
    'PIC-No Report Requested': 'report-pic-n',
  };

  private reportIconVariantByStatus: Record<string, 'danger' | 'success' | 'warning'> = {
    [IApiInvestigationReportStatus.Draft.toUpperCase()]: 'danger',
    [IApiInvestigationReportStatus.Final.toUpperCase()]: 'success',
    [IApiInvestigationReportStatus.EditorReview.toUpperCase()]: 'warning',
    [IApiInvestigationReportStatus.TechReview.toUpperCase()]: 'warning',
    [IApiInvestigationReportStatus.TechReview_2.toUpperCase()]: 'warning',
    [IApiInvestigationReportStatus.ClientReview.toUpperCase()]: 'warning'
  };

  private reportStatusLabelByStatus: Record<string, string> = InvestigationReportStatus;

  public getIcon(riskTypeId: string) {
    return getRiskTypeIcon(riskTypeId);
  }

  public getPriorityIcon(priorityName: string) {
    return getPriorityIcon(priorityName);
  }

  public getPriorityTooltip(priorityName: string) {
    return getPriorityTooltip(priorityName);
  }

  public getReportTypeLabel(report: IApiReportWithStatus): string | null {
    const reportType = (report as any)?.type;
    if (reportType) {
      return reportType;
    }
    return report?.document?.title || null;
  }

  public getReportIconName(report: IApiReportWithStatus): string | null {
    const reportTitle = this.getReportTypeLabel(report);
    if (!reportTitle) {
      return null;
    }
    return this.reportIconByTitle[reportTitle] || null;
  }

  public getReportIconVariant(report: IApiReportWithStatus): 'danger' | 'success' | 'default' | 'warning' {
    const status = report?.document?.status?.toUpperCase();
    return (status && this.reportIconVariantByStatus[status]) || 'default';
  }

  public getReportStatusLabel(report: IApiReportWithStatus): string {
    const status = report?.document?.status?.toUpperCase();
    return (status && this.reportStatusLabelByStatus[status]) || report?.document?.status || 'N/A';
  }

  public onAddComment(): void {
    this.commentAdd.emit(this.dataItem);
  }

  public onOpenReports(event: MouseEvent): void {
    this.documentsReportsOpen.emit({ event, investigation: this.dataItem });
  }

  public navigateTo(tab: string, fragment?: string): void {
    this.router.navigate(['/investigations', this.dataItem?.id], {
      queryParams: { tab },
      ...(fragment ? { fragment } : {})
    });
  }
}
